import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  ArrowBack,
  Cancel,
  CheckCircle,
  Close,
  ExpandLess,
  FileUploadOutlined,
  Send,
} from "@mui/icons-material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  getPOById,
  getPOByCode,
  uploadAttachment,
  reuploadAttachment,
} from "../../services/poService";
import { acknowledgePO } from "../../services/invoiceService";
import { getPODetails } from "./v2/api/invoiceQueries";
import { invoiceKeys } from "./v2/api/queryKeys";
import { convertToINR } from "../../helpers/Utils";
import { useWalletFlag } from "../Payments/hooks/useWalletFlag";
import { getBackendErrorMessage } from "../../helpers/errorUtils";
import { useToast } from "./hooks/useToast";
import { useInvoiceV2Flag } from "./hooks/useInvoiceV2Flag";
import { useIsMobile } from "../../hooks/useIsMobile";
import StickyActionBar from "../../components/mobile/StickyActionBar";
import MobileCard from "../../components/mobile/MobileCard";
import InvoicePreview from "./components/InvoicePreview";
import { TOKENS } from "./constants/figmaTokens";
import { poValueWithTax } from "./utils/poValue";
import { useCreditNoteParse } from "./hooks/useCreditNoteParse";

const stopPropagation = (e) => e.stopPropagation();

function CnInvoiceCard({ inv, checked, onToggle }) {
  return (
    <MobileCard onClick={onToggle} sx={checked ? { borderColor: TOKENS.primaryDefault } : undefined}>
      <Box display="flex" alignItems="center" gap={1}>
        <Checkbox
          size="small"
          checked={checked}
          onChange={onToggle}
          onClick={stopPropagation}
          onKeyDown={stopPropagation}
          sx={{ p: 0.25, ml: -0.5 }}
        />
        <Typography
          sx={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 600, lineHeight: "20px", overflowWrap: "anywhere" }}
        >
          {inv.invoice_number}
        </Typography>
        <Typography sx={{ fontSize: 14, fontWeight: 700, lineHeight: "20px" }}>
          {inv.amount != null ? convertToINR(inv.amount) : "-"}
        </Typography>
      </Box>
      <Typography sx={{ fontSize: 12, lineHeight: "16px", color: TOKENS.onSurfaceSecondary, mt: 0.25 }}>
        {inv.status || "-"}
      </Typography>
    </MobileCard>
  );
}

// One field row: [status icon] [label] [input]
function ValidatedField({ status, label, helper, error, children, compact = false }) {
  // status: "valid" | "invalid" | "none"
  const isValid = status === "valid";
  const isInvalid = status === "invalid";

  if (compact) {
    return (
      <Box display="flex" flexDirection="column" gap="4px">
        <Box display="flex" alignItems="center" gap="4px">
          {isValid && <CheckCircle sx={{ fontSize: 20, color: TOKENS.tertiaryGreenDefault }} />}
          {isInvalid && <Cancel sx={{ fontSize: 20, color: TOKENS.tertiaryRedDefault }} />}
          <Typography
            sx={{ fontSize: 14, fontWeight: 500, lineHeight: "20px", color: TOKENS.onSurfaceSecondary }}
          >
            {label}
          </Typography>
        </Box>
        {children}
        {(error || helper) && (
          <Typography
            sx={{
              fontSize: 12,
              lineHeight: "16px",
              color: error ? TOKENS.tertiaryRedDefault : TOKENS.onSurfaceSecondary,
            }}
          >
            {error || helper}
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Box display="flex" gap="24px" alignItems="flex-start">
      <Box sx={{ width: 178, position: "relative", minHeight: 24 }}>
        <Box
          sx={{
            position: "absolute",
            left: 0,
            top: 0,
            width: 24,
            height: 24,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {isValid && (
            <CheckCircle sx={{ fontSize: 24, color: TOKENS.tertiaryGreenDefault }} />
          )}
          {isInvalid && (
            <Cancel sx={{ fontSize: 24, color: TOKENS.tertiaryRedDefault }} />
          )}
        </Box>
        <Typography
          sx={{
            position: "absolute",
            left: 28,
            top: 2,
            fontSize: 14,
            fontWeight: 500,
            lineHeight: "20px",
            color: TOKENS.onSurfaceSecondary,
            width: 150,
          }}
        >
          {label}
        </Typography>
      </Box>
      <Box sx={{ width: 364, display: "flex", flexDirection: "column", gap: "4px" }}>
        {children}
        {(error || helper) && (
          <Typography
            sx={{
              fontSize: 12,
              lineHeight: "16px",
              color: error ? TOKENS.tertiaryRedDefault : TOKENS.onSurfaceSecondary,
            }}
          >
            {error || helper}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

// Reusable single-line text input styled to match Figma. Pre-filled from the parsed
// credit note when one is available; always editable.
function CnTextInput({ value, onChange, error, placeholder, type = "text", disabled = false, label }) {
  return (
    <TextField
      value={value ?? ""}
      onChange={(e) => onChange?.(e.target.value)}
      placeholder={placeholder}
      type={type}
      disabled={disabled}
      inputProps={label ? { "aria-label": label } : undefined}
      size="small"
      fullWidth
      sx={{
        "& .MuiOutlinedInput-root": {
          height: 36,
          borderRadius: "4px",
          fontSize: 14,
          "& fieldset": {
            borderColor: error ? TOKENS.tertiaryRedDefault : TOKENS.onSurfaceBorder,
          },
          "&:hover fieldset": {
            borderColor: error ? TOKENS.tertiaryRedDefault : TOKENS.onSurfaceBorder,
          },
        },
        "& .MuiInputBase-input": {
          padding: "8px 16px",
          fontSize: 14,
          lineHeight: "20px",
          color: TOKENS.onSurfaceDefault,
        },
      }}
    />
  );
}

export default function CreditNoteSubmission() {
  const { po_code } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const isMobileViewport = useIsMobile();
  const { isV2 } = useInvoiceV2Flag();
  const isMobile = isV2 && isMobileViewport;
  const statePoId = location.state?.poId;

  const poByIdQuery = useQuery({
    queryKey: ["po-by-id", statePoId],
    queryFn: () => getPOById(statePoId),
    enabled: !!statePoId,
    retry: false,
  });

  const poByCodeQuery = useQuery({
    queryKey: ["po-by-code", po_code],
    queryFn: () => getPOByCode(po_code),
    enabled: !statePoId && !!po_code,
    retry: false,
  });

  const poQuery = statePoId ? poByIdQuery : poByCodeQuery;
  const po = poQuery.data;
  const draft = po?.draft;

  // Multiselect: a credit note can be associated with one or more invoices.
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState(() => new Set());
  const [uploadedFile, setUploadedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  // CN form fields, pre-filled from the parsed document (ENG-29736) and always editable.
  const [cnNumber, setCnNumber] = useState("");
  const [cnValue, setCnValue] = useState("");
  const [cnDate, setCnDate] = useState("");
  const [refundUtr, setRefundUtr] = useState("");
  const [attachmentId, setAttachmentId] = useState(null);
  // Presigned S3 URL from the first /attachments POST. Reused for re-uploads so
  // we don't create a fresh attachment record each time the vendor swaps files.
  const [attachmentUrl, setAttachmentUrl] = useState(null);

  const { showToast } = useToast();

  const cnUploaded = !!uploadedFile;

  useEffect(() => {
    if (!uploadedFile) {
      setPreviewUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(uploadedFile);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [uploadedFile]);

  const firstUploadInFlightRef = useRef(false);
  const parseCurrency = (val) => {
    if (val == null) return 0;
    const n = parseFloat(String(val).replace(/[^0-9.-]/g, ""));
    return Number.isFinite(n) ? n : 0;
  };

  const attachmentMutation = useMutation({
    mutationFn: async (file) => {
      if (attachmentId && attachmentUrl) {
        return reuploadAttachment(file, { id: attachmentId, url: attachmentUrl });
      }
      if (firstUploadInFlightRef.current) {
        throw new Error(
          "Upload in progress — please wait for the current upload to finish."
        );
      }
      firstUploadInFlightRef.current = true;
      try {
        return await uploadAttachment(file);
      } finally {
        firstUploadInFlightRef.current = false;
      }
    },
    onSuccess: (data) => {
      setAttachmentId(data?.id || null);
      setAttachmentUrl(data?.url || null);
      // confirm's id: the only one that stays valid after dedup (see poService).
      if (data?.id) cnParse.parse(data.id);
    },
    onError: (err) => {
      showToast({
        type: "error",
        message: `Upload failed: ${err?.message || "unknown error"}`,
      });
    },
  });

  const handleFile = (file) => {
    if (!file) return;
    setUploadedFile(file);
    attachmentMutation.mutate(file);
  };
  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFile(e.dataTransfer.files?.[0]);
  };
  const handleDragOver = (e) => {
    e.preventDefault();
    if (!dragOver) setDragOver(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  // Post-tax, like the invoices and payments it is compared with (ENG-28003).
  const revisedPO = poValueWithTax(draft) ?? 0;
  const approvedInvoiceAmount = po?.meta_data?.invoices_data?.total_invoice_amount ?? 0;
  const expectedCNAmount = Math.max(0, approvedInvoiceAmount - revisedPO);

  const invoices = po?.meta_data?.invoices_data?.invoices ?? [];

  // Pre-fill from the uploaded document (ENG-29736); advisory, never blocks submit.
  const cnParse = useCreditNoteParse({
    poCode: po_code,
    invoices,
    expectedAmount: expectedCNAmount,
    values: { cnNumber, cnValue, cnDate },
    setters: { cnNumber: setCnNumber, cnValue: setCnValue, cnDate: setCnDate, invoices: setSelectedInvoiceIds },
    onFailure: () =>
      showToast({
        type: "info",
        message: "Couldn't read the credit note automatically — please fill in the fields.",
      }),
  });

  // /po-details exposes invoice numeric `id`s (FINS meta_data nested invoices_data does not).
  // We need these to populate the credit_note.invoice_ids (int64 array) the backend expects.
  const poDetailsQuery = useQuery({
    // The shared PO-details key, so a payment-request invalidation refreshes the
    // paid amount here and in the re-upload (CreditNoteReview).
    queryKey: invoiceKeys.poDetails(po_code),
    queryFn: () => getPODetails(po_code),
    enabled: !!po_code,
    retry: false,
    staleTime: 60_000,
  });
  const invoiceIdByNumber = useMemo(() => {
    const list = poDetailsQuery.data?.po_list?.[0]?.invoices ?? [];
    const map = new Map();
    list.forEach((inv) => {
      if (inv?.invoice_no != null && inv?.id != null) {
        map.set(inv.invoice_no, inv.id);
      }
    });
    return map;
  }, [poDetailsQuery.data]);

  // Paid amount comes from the PO details response (ENG-27224): the backend's
  // `total_amount_paid` is the union of legacy CSV payments and v2 UTR lines,
  // and is the same figure the backend's own refund-UTR check uses.
  const totalPaid = poDetailsQuery.data?.po_list?.[0]?.total_amount_paid ?? 0;
  const requiresRefundUtr = totalPaid > revisedPO;
  // Open advance (ENG-27318): with the wallet on, an overpaid vendor may leave
  // the UTR blank and the backend parks the excess as credit against their next
  // payment. With it off the field stays required, exactly as before. If VIP
  // cannot park (its own flag off, or no facility), its 400 names the excess.
  // The values below are only read where `requiresRefundUtr` holds. The UTR is
  // trimmed so a stray space counts as blank: VIP would park it anyway, but it
  // would store " " as the UTR and a re-upload would then demand one.
  const refundUtrOptional = useWalletFlag();
  const trimmedRefundUtr = refundUtr.trim();
  const refundUtrMissing = !refundUtrOptional && !trimmedRefundUtr;
  const overpaidAmount = totalPaid - revisedPO;

  const sendL2Mutation = useMutation({
    mutationFn: () => {
      // Map selected invoice_numbers → invoice ids (backend-expected numeric ids).
      const invoiceIds = Array.from(selectedInvoiceIds)
        .map((invoiceNumber) => invoiceIdByNumber.get(invoiceNumber))
        .filter((id) => id != null);
      const data = [
        {
          po_code: po_code,
          po_id: po?.id,
          status: "approve",
          credit_note: {
            invoice_ids: invoiceIds,
            credit_note_number: cnNumber,
            attachment_id: attachmentId,
            credit_note_value: parseCurrency(cnValue),
            ...cnParse.ackFields(cnDate),
            ...(requiresRefundUtr && trimmedRefundUtr ? { utr_number: trimmedRefundUtr } : {}),
          },
        },
      ];
      return acknowledgePO({ data });
    },
    onSuccess: () => {
      showToast({ type: "success", message: "Credit note sent for L2 approval." });
      navigate("/dashboard/invoice-management");
    },
    onError: (err) => {
      showToast({
        type: "error",
        message: `Submission failed: ${getBackendErrorMessage(err, "unknown error")}`,
      });
    },
  });

  const canSubmit =
    !!po &&
    !!draft &&
    !!attachmentId &&
    selectedInvoiceIds.size > 0 &&
    !!cnNumber &&
    !!cnValue &&
    !poDetailsQuery.isLoading &&
    !poDetailsQuery.isError &&
    (!requiresRefundUtr || refundUtrOptional || !!trimmedRefundUtr) &&
    !sendL2Mutation.isLoading &&
    !attachmentMutation.isLoading;

  const handleSendForL2 = () => sendL2Mutation.mutate();
  // Auto-select when there's exactly one invoice (vendor doesn't need to click).
  useEffect(() => {
    if (invoices.length === 1 && selectedInvoiceIds.size === 0) {
      setSelectedInvoiceIds(new Set([invoices[0].invoice_number]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoices.length]);

  const allSelected =
    invoices.length > 0 && selectedInvoiceIds.size === invoices.length;
  const someSelected = selectedInvoiceIds.size > 0 && !allSelected;
  const toggleInvoice = (id) => {
    cnParse.noteEdited("invoices");
    setSelectedInvoiceIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    cnParse.noteEdited("invoices");
    setSelectedInvoiceIds(
      allSelected ? new Set() : new Set(invoices.map((i) => i.invoice_number))
    );
  };

  const cnValuePrefilledRef = useRef(false);
  useEffect(() => {
    if (po && !cnValuePrefilledRef.current && expectedCNAmount > 0) {
      setCnValue(expectedCNAmount.toFixed(2));
      cnValuePrefilledRef.current = true;
    }
  }, [po, expectedCNAmount]);

  const { parsing, warnings, fieldStatus, summary, markEdited } = cnParse;

  const actionButtons = (
    <>
      <Button
        variant="contained"
        onClick={() => navigate(`/dashboard/invoice-management/po/${encodeURIComponent(po_code)}/revision`)}
        startIcon={<Close />}
        sx={{
          bgcolor: TOKENS.surfaceVariant,
          color: TOKENS.onSurfaceSecondary,
          boxShadow: "none",
          height: 36,
          textTransform: "none",
          "&:hover": { bgcolor: TOKENS.surfaceVariant },
        }}
      >
        Cancel
      </Button>
      <Button
        variant="contained"
        aria-label="Send for L2 Approval"
        onClick={handleSendForL2}
        disabled={!canSubmit}
        startIcon={<Send />}
        sx={{
          bgcolor: TOKENS.primaryDefault,
          color: "#FFFFFF",
          boxShadow: "none",
          height: isMobile ? "auto" : 36,
          minHeight: 36,
          textTransform: "none",
          opacity: canSubmit ? 1 : 0.5,
          "&:hover": { bgcolor: TOKENS.primaryDefault },
          "&.Mui-disabled": {
            bgcolor: TOKENS.primaryDefault,
            color: "#FFFFFF",
            opacity: 0.5,
          },
        }}
      >
        {isMobile ? "Send for L2" : "Send for L2 Approval"}
      </Button>
    </>
  );

  return (
    <Box
      sx={
        isMobile
          ? { bgcolor: TOKENS.surfaceBg, display: "flex", flexDirection: "column", height: "calc(100vh - 48px)" }
          : { bgcolor: TOKENS.surfaceBg, minHeight: "100vh" }
      }
    >
      {/* Inner header */}
      <Box
        sx={{
          bgcolor: TOKENS.surfaceDefault,
          boxShadow: "0px 0px 1px rgba(0,0,0,0.2)",
          display: "flex",
          alignItems: "center",
          gap: isMobile ? "8px" : "24px",
          pl: "8px",
          pr: isMobile ? "12px" : "24px",
          py: "8px",
          flexShrink: 0,
        }}
      >
        <Box display="flex" alignItems="center" gap="8px" flex={1}>
          <IconButton onClick={() => navigate(`/dashboard/invoice-management/po/${encodeURIComponent(po_code)}/revision`)} sx={{ width: 36, height: 36 }}>
            <ArrowBack sx={{ fontSize: 24, color: TOKENS.onSurfaceSecondary }} />
          </IconButton>
          <Box display="flex" flexDirection="column" justifyContent="center">
            <Typography
              sx={{ fontSize: 12, lineHeight: "16px", color: TOKENS.onSurfaceSecondary }}
            >
              {po_code}/
            </Typography>
            <Typography
              sx={{
                fontSize: 16,
                fontWeight: 600,
                lineHeight: "24px",
                color: TOKENS.onSurfaceDefault,
              }}
            >
              Credit note details
            </Typography>
            {isMobile && (
              <Typography
                sx={{ fontSize: 12, lineHeight: "16px", color: TOKENS.onSurfaceSecondary }}
              >
                Expected credit note amount · {convertToINR(expectedCNAmount)}
              </Typography>
            )}
          </Box>
        </Box>

        {!isMobile && (
          <Box display="flex" alignItems="center" gap="16px">
            <Box display="flex" flexDirection="column" alignItems="flex-end">
              <Typography
                sx={{ fontSize: 10, lineHeight: "12px", color: TOKENS.onSurfaceSecondary }}
              >
                Expected credit note amount
              </Typography>
              <Typography
                sx={{
                  fontSize: 16,
                  fontWeight: 700,
                  lineHeight: "24px",
                  color: TOKENS.onSurfaceDefault,
                }}
              >
                {convertToINR(expectedCNAmount)}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1}>
              {actionButtons}
            </Stack>
          </Box>
        )}
      </Box>

      {/* Body */}
      <Box
        sx={{
          p: isMobile ? "12px" : "24px",
          display: "flex",
          flexDirection: "column",
          gap: isMobile ? "16px" : "24px",
          ...(isMobile && {
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            WebkitOverflowScrolling: "touch",
            "& > *": { flexShrink: 0 },
          }),
        }}
      >
        {/* Invoice selection — placeholder layout, will replace once Figma lands */}
        {po && (
          <Box>
            <Box display="flex" alignItems="baseline" gap="8px" mb={1.5}>
              <Typography
                sx={{
                  fontSize: 16,
                  fontWeight: 700,
                  lineHeight: "24px",
                  color: TOKENS.onSurfaceDefault,
                }}
              >
                Select invoices for credit note
              </Typography>
              <Typography
                sx={{ fontSize: 12, lineHeight: "16px", color: TOKENS.onSurfaceSecondary }}
              >
                ({invoices.length})
              </Typography>
              {selectedInvoiceIds.size > 0 && (
                <Typography
                  sx={{
                    fontSize: 12,
                    lineHeight: "16px",
                    color: TOKENS.primaryDefault,
                    marginLeft: "auto",
                  }}
                >
                  {selectedInvoiceIds.size} selected
                </Typography>
              )}
            </Box>
            <Card
              sx={{
                bgcolor: TOKENS.surfaceDefault,
                borderRadius: "8px",
                boxShadow: "none",
                overflow: "hidden",
              }}
            >
              {invoices.length === 0 ? (
                <Box sx={{ p: "24px", textAlign: "center" }}>
                  <Typography sx={{ fontSize: 14, color: TOKENS.onSurfaceSecondary }}>
                    No invoices found on this PO.
                  </Typography>
                </Box>
              ) : isMobile ? (
                <Box sx={{ p: "12px", display: "flex", flexDirection: "column", gap: "8px" }}>
                  <FormControlLabel
                    sx={{ ml: 0 }}
                    label={<Typography fontSize="14px">Select all</Typography>}
                    control={
                      <Checkbox
                        size="small"
                        checked={allSelected}
                        indeterminate={someSelected}
                        onChange={toggleAll}
                      />
                    }
                  />
                  {invoices.map((inv) => (
                    <CnInvoiceCard
                      key={inv.invoice_number}
                      inv={inv}
                      checked={selectedInvoiceIds.has(inv.invoice_number)}
                      onToggle={() => toggleInvoice(inv.invoice_number)}
                    />
                  ))}
                </Box>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ borderBottom: `2px solid ${TOKENS.onSurfaceBorder}` }}>
                      <TableCell sx={{ width: 56, padding: "8px 16px" }}>
                        <Checkbox
                          checked={allSelected}
                          indeterminate={someSelected}
                          onChange={toggleAll}
                          size="small"
                          sx={{ p: 0 }}
                        />
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: 12,
                          fontWeight: 500,
                          textTransform: "uppercase",
                          color: TOKENS.onSurfaceSecondary,
                        }}
                      >
                        Invoice Number
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontSize: 12,
                          fontWeight: 500,
                          textTransform: "uppercase",
                          color: TOKENS.onSurfaceSecondary,
                        }}
                      >
                        Amount
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: 12,
                          fontWeight: 500,
                          textTransform: "uppercase",
                          color: TOKENS.onSurfaceSecondary,
                        }}
                      >
                        Status
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {invoices.map((inv) => {
                      const id = inv.invoice_number;
                      const checked = selectedInvoiceIds.has(id);
                      return (
                        <TableRow
                          key={id}
                          hover
                          onClick={() => toggleInvoice(id)}
                          sx={{ cursor: "pointer" }}
                        >
                          <TableCell sx={{ padding: "8px 16px" }}>
                            <Checkbox
                              checked={checked}
                              onChange={() => toggleInvoice(id)}
                              onClick={(e) => e.stopPropagation()}
                              size="small"
                              sx={{ p: 0 }}
                            />
                          </TableCell>
                          <TableCell sx={{ fontSize: 14, color: TOKENS.onSurfaceDefault }}>
                            {inv.invoice_number}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ fontSize: 14, fontWeight: 500, color: TOKENS.onSurfaceDefault }}
                          >
                            {inv.amount != null ? convertToINR(inv.amount) : "-"}
                          </TableCell>
                          <TableCell sx={{ fontSize: 14, color: TOKENS.onSurfaceDefault }}>
                            {inv.status || "-"}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </Card>
          </Box>
        )}

        {/* Upload empty state — shown until a CN is uploaded */}
        {po && !cnUploaded && (
          <Box
            onClick={() => fileInputRef.current?.click()}
            onDragOver={handleDragOver}
            onDragEnter={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            sx={{
              bgcolor: dragOver ? "rgba(54, 85, 242, 0.04)" : TOKENS.surfaceDefault,
              border: `1px ${dragOver ? "dashed" : "solid"} ${
                dragOver ? TOKENS.primaryDefault : TOKENS.onSurfaceBorder
              }`,
              borderRadius: "8px",
              height: isMobile ? 180 : 228,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: "16px",
              p: "16px",
              cursor: "pointer",
              transition: "background-color 120ms, border-color 120ms",
              "&:hover": { bgcolor: "rgba(54, 85, 242, 0.02)" },
            }}
          >
            <Typography
              sx={{
                fontSize: 20,
                fontWeight: 700,
                lineHeight: "24px",
                color: TOKENS.onSurfaceDefault,
              }}
            >
              Upload Credit Note
            </Typography>
            <FileUploadOutlined sx={{ fontSize: 36, color: TOKENS.primaryDefault }} />
            <Box sx={{ textAlign: "center" }}>
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 600,
                  lineHeight: "20px",
                  color: TOKENS.onSurfaceSecondary,
                }}
              >
                Drop files here or
              </Typography>
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 600,
                  lineHeight: "20px",
                  color: TOKENS.onSurfaceSecondary,
                }}
              >
                click to upload
              </Typography>
            </Box>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,image/*"
              onChange={(e) => handleFile(e.target.files?.[0])}
              style={{ display: "none" }}
            />
          </Box>
        )}

        {/* Two columns: preview + fields — shown once CN is uploaded */}
        {po && cnUploaded && (
        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            gap: isMobile ? "16px" : "24px",
            alignItems: isMobile ? "stretch" : "flex-start",
          }}
        >
          {/* Left: uploaded credit note preview */}
        <Box sx={{ flex: isMobile ? "none" : 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: 16,
              fontWeight: 700,
              lineHeight: "24px",
              color: TOKENS.onSurfaceDefault,
              mb: 1.5,
            }}
          >
            Uploaded credit note
          </Typography>
          <Box
            sx={{
              bgcolor: TOKENS.surfaceDefault,
              borderRadius: "8px",
              minHeight: isMobile ? 320 : 800,
              overflow: "hidden",
            }}
          >
            {previewUrl && uploadedFile ? (
              <InvoicePreview
                url={previewUrl}
                type={uploadedFile.name.split(".").pop()?.toLowerCase()}
                scale={isMobile ? 1 : 1.2}
              />
            ) : (
              <Box
                sx={{
                  height: isMobile ? 320 : 800,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: TOKENS.onSurfaceSecondary,
                  fontSize: 14,
                }}
              >
                (Preview unavailable)
              </Box>
            )}
          </Box>
        </Box>

        {/* Right: credit note fields form */}
        <Box sx={{ flex: isMobile ? "none" : 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: 16,
              fontWeight: 700,
              lineHeight: "24px",
              color: TOKENS.onSurfaceDefault,
              mb: 1.5,
            }}
          >
            Credit note fields
          </Typography>

          {poQuery.isLoading && (
            <Box display="flex" alignItems="center" gap={1}>
              <CircularProgress size={20} /> Loading PO from FINS…
            </Box>
          )}
          {poQuery.isError && (
            <Alert severity="error">
              FINS PO fetch failed: {poQuery.error?.message || "unknown error"}
            </Alert>
          )}

          {po && !draft && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              No revision draft on this PO (has_draft = false). Credit note submission expects a revised PO — please return to the PO list and try again.
            </Alert>
          )}

          {po && requiresRefundUtr && (
            <Card
              sx={{
                bgcolor: TOKENS.surfaceDefault,
                borderRadius: "8px",
                boxShadow: "none",
                px: isMobile ? "16px" : "24px",
                py: "16px",
                mb: 2,
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                alignItems: isMobile ? "stretch" : "center",
                gap: isMobile ? "8px" : "24px",
              }}
            >
              <Typography
                sx={{
                  width: isMobile ? "auto" : 178,
                  fontSize: 14,
                  fontWeight: 500,
                  lineHeight: "20px",
                  color: TOKENS.onSurfaceSecondary,
                  flexShrink: 0,
                }}
              >
                Refund UTR Number{refundUtrOptional ? " (optional)" : ""}
              </Typography>
              <Box sx={{ width: isMobile ? "100%" : 364 }}>
                <TextField
                  value={refundUtr}
                  onChange={(e) => setRefundUtr(e.target.value)}
                  placeholder="e.g. ICIC0123456789"
                  size="small"
                  fullWidth
                  error={refundUtrMissing}
                  helperText={
                    refundUtrOptional
                      ? `Enter the UTR if you have transferred ${convertToINR(overpaidAmount)} back. Leave blank to hold it as credit against your next payment.`
                      : refundUtrMissing
                        ? "Required"
                        : ""
                  }
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      height: 36,
                      borderRadius: "4px",
                      fontSize: 14,
                      "& fieldset": {
                        borderColor: refundUtrMissing
                          ? TOKENS.tertiaryRedDefault
                          : TOKENS.onSurfaceBorder,
                      },
                      "&:hover fieldset": {
                        borderColor: refundUtrMissing
                          ? TOKENS.tertiaryRedDefault
                          : TOKENS.onSurfaceBorder,
                      },
                    },
                    "& .MuiInputBase-input": {
                      padding: "8px 16px",
                      fontSize: 14,
                      lineHeight: "20px",
                      color: TOKENS.onSurfaceDefault,
                    },
                    "& .MuiInputBase-input::placeholder": {
                      color: TOKENS.onSurfaceSecondary,
                      opacity: 1,
                    },
                    "& .MuiFormHelperText-root": {
                      marginLeft: 0,
                      fontSize: 12,
                      color: refundUtrMissing
                        ? TOKENS.tertiaryRedDefault
                        : TOKENS.onSurfaceSecondary,
                    },
                  }}
                />
              </Box>
            </Card>
          )}

          {po && (
            <Card
              sx={{
                bgcolor: TOKENS.surfaceDefault,
                borderRadius: "8px",
                boxShadow: "none",
                py: 2,
              }}
            >
              {/* Credit note row: label + filename link + Re-Upload */}
              <Box
                display="flex"
                alignItems="center"
                flexWrap={isMobile ? "wrap" : "nowrap"}
                gap={isMobile ? "8px" : "24px"}
                px={isMobile ? "16px" : "24px"}
                py={1}
              >
                <Typography
                  sx={{
                    width: isMobile ? "auto" : 178,
                    fontSize: 14,
                    fontWeight: 500,
                    lineHeight: "20px",
                    color: TOKENS.onSurfaceSecondary,
                  }}
                >
                  Credit note
                </Typography>
                <Typography
                  sx={{
                    fontSize: 14,
                    fontWeight: 600,
                    lineHeight: "20px",
                    color: TOKENS.onSurfaceDefault,
                  }}
                >
                  {uploadedFile?.name || "credit_note"}
                </Typography>
                <Box sx={{ flex: 1 }} />
                <Box
                  onClick={() => fileInputRef.current?.click()}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    color: TOKENS.primaryDefault,
                    cursor: "pointer",
                  }}
                >
                  <FileUploadOutlined sx={{ fontSize: 20 }} />
                  <Typography sx={{ fontSize: 14, fontWeight: 600, lineHeight: "20px" }}>
                    Re-Upload
                  </Typography>
                </Box>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,image/*"
                  onChange={(e) => handleFile(e.target.files?.[0])}
                  style={{ display: "none" }}
                />
              </Box>

              <Box px={isMobile ? "16px" : "24px"} py="8px">
                <Divider sx={{ borderColor: TOKENS.surfaceVariant }} />
              </Box>

              {/* "Credit note fields" sub-heading (collapsible) */}
              <Box display="flex" alignItems="center" gap="4px" px={isMobile ? "16px" : "24px"} py={1}>
                <ExpandLess sx={{ fontSize: 20, color: TOKENS.onSurfaceSecondary }} />
                <Typography
                  sx={{
                    fontSize: 14,
                    fontWeight: 700,
                    lineHeight: "20px",
                    color: TOKENS.onSurfaceDefault,
                  }}
                >
                  Credit note fields
                </Typography>
              </Box>

              {parsing && (
                <Box display="flex" alignItems="center" gap={1} px={isMobile ? "16px" : "24px"} py={1}>
                  <CircularProgress size={16} />
                  <Typography sx={{ fontSize: 14, color: TOKENS.onSurfaceSecondary }}>
                    Reading credit note…
                  </Typography>
                </Box>
              )}
              {(warnings.po || warnings.invoices) && (
                <Box display="flex" flexDirection="column" gap={1} px={isMobile ? "16px" : "24px"} py={1}>
                  {warnings.po && <Alert severity="warning">{warnings.po}</Alert>}
                  {warnings.invoices && <Alert severity="warning">{warnings.invoices}</Alert>}
                </Box>
              )}

              {/* Field rows */}
              <Box display="flex" flexDirection="column" gap={3} px={isMobile ? "16px" : "24px"} py={1}>
                <ValidatedField
                  status={fieldStatus("cnNumber")}
                  label="Credit note number"
                  error={!cnNumber ? "Required" : null}
                  compact={isMobile}
                >
                  <CnTextInput
                    value={cnNumber}
                    onChange={markEdited("cnNumber", setCnNumber)}
                    placeholder="Enter credit note number"
                    error={!cnNumber}
                    disabled={parsing}
                    label="Credit note number"
                  />
                </ValidatedField>

                <ValidatedField
                  status={fieldStatus("cnValue", warnings.value)}
                  label="Credit note value"
                  error={!cnValue ? "Required" : warnings.value}
                  compact={isMobile}
                >
                  <CnTextInput
                    value={cnValue}
                    onChange={markEdited("cnValue", setCnValue)}
                    placeholder={convertToINR(expectedCNAmount)}
                    error={!cnValue}
                    disabled={parsing}
                    label="Credit note value"
                  />
                </ValidatedField>

                <ValidatedField
                  status={fieldStatus("cnDate")}
                  label="Credit note date"
                  compact={isMobile}
                >
                  <CnTextInput
                    value={cnDate}
                    onChange={markEdited("cnDate", setCnDate)}
                    type="date"
                    disabled={parsing}
                    label="Credit note date"
                  />
                </ValidatedField>

                {summary.length > 0 && (
                  <Box display="flex" flexDirection="column" gap="4px" data-testid="cn-parse-summary">
                    {summary.map(([label, value]) => (
                      <Typography key={label} sx={{ fontSize: 13, lineHeight: "18px", color: TOKENS.onSurfaceSecondary }}>
                        {label}: <Box component="span" sx={{ color: TOKENS.onSurfaceDefault }}>{value}</Box>
                      </Typography>
                    ))}
                  </Box>
                )}
              </Box>
            </Card>
          )}
        </Box>
        </Box>
        )}
      </Box>

      {isMobile && <StickyActionBar>{actionButtons}</StickyActionBar>}
    </Box>
  );
}
